import { Bell, CheckCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { timeAgo } from '../lib/format.ts';
import { useMarkRead, useNotificationList, useUnreadCount } from '../lib/notifications.ts';
import type { AppNotification } from '../lib/types.ts';
import { Popover } from './ui/popover.tsx';

export function UnreadDot({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="absolute -top-0.5 -right-0.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-danger px-1 text-[11px] leading-none font-bold text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function NotificationItem({
  n,
  onOpen,
}: {
  n: AppNotification;
  onOpen: (n: AppNotification) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(n)}
      className="flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2"
    >
      <span
        className={`mt-1.5 size-2 shrink-0 rounded-full ${n.readAt ? 'bg-transparent' : 'bg-brand'}`}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className={`block text-sm ${n.readAt ? '' : 'font-semibold'}`}>{n.title}</span>
        {n.body && <span className="mt-0.5 line-clamp-2 block text-sm text-muted">{n.body}</span>}
        <span className="mt-1 block text-xs text-muted">{timeAgo(n.createdAt)}</span>
      </span>
      {!n.readAt && <span className="sr-only">(unread)</span>}
    </button>
  );
}

export function useOpenNotification() {
  const navigate = useNavigate();
  const markRead = useMarkRead();
  return (n: AppNotification) => {
    if (!n.readAt) markRead.mutate([n.id]);
    if (n.url) navigate(n.url);
  };
}

export function NotificationBell() {
  const unread = useUnreadCount(true).data ?? 0;
  const open = useOpenNotification();
  const markRead = useMarkRead();

  return (
    <Popover
      label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
      trigger={
        <>
          <Bell className="size-5" aria-hidden />
          <UnreadDot count={unread} />
        </>
      }
      className="w-[min(24rem,calc(100vw-2rem))]"
    >
      {(close) => (
        <BellPanel
          unread={unread}
          onOpen={(n) => {
            close();
            open(n);
          }}
          onMarkAll={() => markRead.mutate(undefined)}
          onClose={close}
        />
      )}
    </Popover>
  );
}

function BellPanel({
  unread,
  onOpen,
  onMarkAll,
  onClose,
}: {
  unread: number;
  onOpen: (n: AppNotification) => void;
  onMarkAll: () => void;
  onClose: () => void;
}) {
  const list = useNotificationList(8);
  const items = list.data?.items ?? [];
  return (
    <div>
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <p className="font-semibold">Notifications</p>
        {unread > 0 && (
          <button
            type="button"
            onClick={onMarkAll}
            className="flex items-center gap-1 text-sm text-brand hover:underline"
          >
            <CheckCheck className="size-4" aria-hidden />
            Mark all read
          </button>
        )}
      </div>
      <div className="max-h-[60vh] divide-y divide-line overflow-y-auto">
        {list.isPending && <p className="px-4 py-6 text-center text-sm text-muted">Loading…</p>}
        {!list.isPending && items.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-muted">You’re all caught up.</p>
        )}
        {items.map((n) => (
          <NotificationItem key={n.id} n={n} onOpen={onOpen} />
        ))}
      </div>
      <Link
        to="/notifications"
        onClick={onClose}
        className="block border-t border-line px-4 py-2.5 text-center text-sm font-medium text-brand hover:bg-surface-2"
      >
        See all notifications
      </Link>
    </div>
  );
}
